/**
 * @module useLocalStorage
 * @description Custom hook for persisting state in localStorage with React
 */

import { useState, useEffect } from 'react';

/**
 * @function UseLocalStorage
 * @description Provides state that automatically syncs with localStorage
 *
 * @param {string} key - The key under which the value is stored in localStorage
 * @param {*} initialValue - The value to use if no value is found in localStorage
 *
 * @returns {Array} - A stateful value and a function to update it:
 *   [0] {*} storedValue - The current value (from localStorage or initialValue)
 *   [1] {Function} setValue - Function to update the stored value
 *
 * @example
 * const [theme, setTheme] = UseLocalStorage('theme', 'light');
 *
 * // Later in your component:
 * return (
 *   <button onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
 *     Toggle theme
 *   </button>
 * );
 */
const UseLocalStorage = (key, initialValue) => {
  const isClientSide = typeof window !== 'undefined';
  const [storedValue, setStoredValue] = useState(initialValue);

  useEffect(() => {
    if (isClientSide) {
      const currentStoredValue = JSON.parse(
        localStorage.getItem(key) || JSON.stringify(initialValue),
      );
      setStoredValue(currentStoredValue);
    }
  }, [isClientSide, key, initialValue]);

  /**
   * @function setValue
   * @description Updates both the React state and localStorage
   * @param {*} value - New value to store
   */
  const setValue = (value) => {
    setStoredValue(value);
    if (isClientSide) {
      localStorage.setItem(key, JSON.stringify(value));
    }
  };

  return [storedValue, setValue];
};

export default UseLocalStorage;
